/*
 * AI-GENERATED
 * Microsoft 365 Copilot
 * LLM: GPT-5 reasoning
 * Tarkistanut Arttu Pietarinen
 * 2026-09-15
 */
import strings from '../../../translations';
import { useAppSelector } from '../../../state/hooks';
import { useEffect, useState, useMemo } from 'react';
import styled from 'styled-components';
import { motion } from 'motion/react';
import { faAngleDown, faAngleUp } from '@fortawesome/free-solid-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { dropdownVariants } from '../utils/SearchUtil';
import useIndexSearch from '../utils/useIndexSearch';

const StyledDropDown = styled(motion.div)`
  width: 100%;
  padding: 0 1px;
  overflow: auto;
  background-color: ${(props) => props.theme.colors.mainWhite};
`;
const StyledItemButton = styled.button`
  display: flex;
  width: 100%;
  align-items: center;
  justify-content: space-between;
  margin-top: 0.5em;
  padding: 0.5em 1em;
  border: 0;
  border-radius: 5px;
  color: ${(props) => props.theme.colors.mainWhite};
  background-color: ${(props) =>
    props.$selected
      ? props.theme.colors.hover ?? '#ffeb3b'
      : props.theme.colors.mainColor1};
  cursor: pointer;
  text-align: left;
  &:hover {
    background-color: ${(props) => props.theme.colors.hover};
    color: ${(props) => props.theme.colors.mainColor1};
  }
`;
const StyledTitle = styled.span`
  overflow: hidden;
  font-size: 14px;
  font-weight: 600;
  text-overflow: ellipsis;
  white-space: nowrap;
`;
const StyledAttributes = styled.dl`
  margin: 0;
`;
const StyledAttribute = styled.div`
  display: grid;
  grid-template-columns: minmax(100px, 1fr) minmax(0, 2fr);
  gap: 0.5em;
  padding: 4px;
  border-bottom: 1px solid #ccc;
`;
const StyledKey = styled.dt`
  overflow: hidden;
  font-weight: 600;
  text-overflow: ellipsis;
`;
const StyledValue = styled.dd`
  overflow-wrap: anywhere;
  margin: 0;
`;

const StyledHighlight = styled.mark`
  padding: 0 2px;
  border-radius: 2px;
  color: inherit;
  background-color: ${(props) =>
    props.theme.colors.secondaryColorYellow ?? '#ffeb3b'};
  font-weight: 600;
`;

const StyledMessage = styled.div`
  margin-top: 8px;
  text-align: center;
`;
const StyledError = styled(StyledMessage)`
  color: ${(props) => props.theme.colors.secondaryColorDarkOrange || '#c55'};
`;

const StyledShowMoreButtonWrapper = styled.div`
  text-align: center;
  margin-top: 1em;
`;

const StyledShowMoreButton = styled.button`
  width: 250px;
  height: 35px;
  color: ${(props) => props.theme.colors.mainWhite};
  background-color: ${(props) => props.theme.colors.mainColor1};
  border-radius: 20px;
  box-shadow: 0px 1px 3px #0000001f;
  border: none;
`;

const StyledResultCount = styled.div`
  padding: 0.75em 1em 0.25em;
  color: ${(props) => props.theme.colors.darkGrey || '#555'};
  font-size: 0.9rem;
  font-weight: 600;
`;

const getItemKey = (item) => `${item.searchMetadataId}:${item.entityId}`;

const getRawAttributes = (item) => item.fulltextJson ?? {};

//helper function to fetch localized name
const getLocalizedFieldName = (fieldName, appLayerLocale) => {
  const overwrittenFieldNameLocales =
    appLayerLocale?.overwrittenFieldNameLocalesJson ?? {};
  return (
    Object.values(overwrittenFieldNameLocales)
      .filter(
        (fieldNameLocales) =>
          fieldNameLocales &&
          typeof fieldNameLocales === 'object' &&
          !Array.isArray(fieldNameLocales)
      )
      .map((fieldNameLocales) => fieldNameLocales[fieldName])
      .find(
        (localizedFieldName) =>
          typeof localizedFieldName === 'string' &&
          localizedFieldName.trim() !== ''
      ) ?? fieldName
  );
};

const getAttributes = (item, appLayerLocale) =>
  Object.entries(getRawAttributes(item)).map(([key, value]) => ({
    key,
    displayKey: getLocalizedFieldName(key, appLayerLocale),
    value
  }));

const getTitle = (item) => {
  const attributes = getRawAttributes(item);
  return (
    attributes.nimi ??
    attributes.name ??
    attributes.title ??
    attributes.oid ??
    item.entityId
  );
};

const formatValue = (value) => {
  if (value === null || value === undefined || value === '') return '-';
  return typeof value === 'object' ? JSON.stringify(value) : String(value);
};

const IndexSearchResultPanel = ({ onResultSelect }) => {
  const {
    status = 'idle',
    query = '',
    items = [],
    error = null,
    paging = { limit: 100, offset: 0, returned: 0, totalCount: 0 }
  } = useAppSelector((state) => state.rpc.indexSearchResults);

  const { currentMetadata } = useAppSelector((state) => state.rpc);

  const escapeRegExp = (value) => value.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');

  const highlightQuery = (value, query) => {
    const formattedValue = formatValue(value);
    const normalizedQuery = query.trim();

    if (!normalizedQuery || formattedValue === '-') {
      return formattedValue;
    }

    const pattern = new RegExp(`(${escapeRegExp(normalizedQuery)})`, 'gi');

    return formattedValue.split(pattern).map((part, index) => {
      const isMatch =
        part.localeCompare(normalizedQuery, undefined, {
          sensitivity: 'accent'
        }) === 0;

      if (!isMatch) {
        return part;
      }

      return <StyledHighlight key={`${index}-${part}`}>{part}</StyledHighlight>;
    });
  };

  const { channel } = useAppSelector((state) => state.rpc);

  const { loadNextPage } = useIndexSearch();

  const [openItemKey, setOpenItemKey] = useState(null);
  const [selectedItemKey, setSelectedItemKey] = useState(null);
  const [appLayerLocale, setAppLayerLocale] = useState(null);

  const hasNextPage = status === 'succeeded' && paging.hasMoreResults;

  const SEARCH_RESULTS_LAYER_ID = 'feature-search-results';

  const showFeaturesOnMap = (channel, geoJson) => {
    channel?.postRequest('MapModulePlugin.RemoveFeaturesFromMapRequest', [
      null,
      null,
      SEARCH_RESULTS_LAYER_ID
    ]);

    if (geoJson.features.length === 0) {
      return;
    }

    channel?.postRequest('MapModulePlugin.AddFeaturesToMapRequest', [
      geoJson,
      {
        layerId: SEARCH_RESULTS_LAYER_ID,
        centerTo: true,
        maxZoomLevel: 13,
        cursor: 'pointer',
        featureStyle: {
          fill: {
            color: 'rgba(229, 0, 131, 1)'
          },
          stroke: {
            color: 'rgba(229, 0, 131, 1)',
            width: 5,
            lineDash: 'solid',
            lineJoin: 'round'
          },
          image: {
            shape: 2,
            size: 5,
            fill: {
              color: 'rgba(229, 0, 131, 1)'
            }
          }
        }
      }
    ]);
  };

  const createFeatureCollection = (items) => ({
    type: 'FeatureCollection',
    features: items
      .filter((item) => item.geometry !== null && item.geometry !== undefined)
      .map((item) => ({
        type: 'Feature',
        id: item.id,
        geometry: item.geometry,
        properties: {
          entityId: item.entityId,
          entityType: item.entityType
        }
      }))
  });

  //show items on map
  const searchResultGeoJson = useMemo(
    () => createFeatureCollection(items ?? []),
    [items]
  );

  const toggleItem = (item) => {
    const itemKey = getItemKey(item);
    const isSelected = selectedItemKey === itemKey;
    const nextItemKey = openItemKey === itemKey ? null : itemKey;

    setOpenItemKey(nextItemKey);
    setSelectedItemKey(isSelected ? null : itemKey);
    showFeaturesOnMap(
      channel,
      isSelected ? searchResultGeoJson : createFeatureCollection([item])
    );

    if (!isSelected) onResultSelect?.(item);
  };

  useEffect(() => {
    if (!channel) {
      return;
    }
    console.info('create geojson', searchResultGeoJson);

    const start = performance.now();

    showFeaturesOnMap(channel, searchResultGeoJson);
    const durationMs = performance.now() - start;

    console.log(`show on map called ${durationMs.toFixed(2)} ms`);
  }, [channel, searchResultGeoJson]);

  useEffect(() => {
    if (!channel || !currentMetadata) {
      return;
    }
    //Fetch applayer locales
    console.info('currentMetadata', currentMetadata.metadata);
    const parsedLayerName = `${currentMetadata.metadata.gsWorkspace}:${currentMetadata.metadata.gsName}`;
    channel.getApplayerLocales(
      [parsedLayerName],
      (data) => {
        console.info('return value of get applayer locales', data);
        if (!data || !Array.isArray(data)) {
          setAppLayerLocale(null);
        } else if (data && data.length === 1) {
          setAppLayerLocale(data[0]);
        } else {
          console.warn(
            'Found multiple rows from app_layer_locale table with value: ',
            parsedLayerName
          );
          setAppLayerLocale(data[0]);
        }
      },
      (error) => {
        console.log('error', error);
        setAppLayerLocale(null);
      }
    );
  }, [channel, currentMetadata]);

  return (
    <>
      <StyledDropDown
        variants={dropdownVariants}
        initial="initial"
        animate="animate"
        exit="exit"
        transition="transition"
      >
        {error && <StyledError role="alert">{error}</StyledError>}
        {status === 'succeeded' && items && paging?.totalCount > 0 && (
          <StyledResultCount>
            {strings.search.searchIndex.totalCount}
            {paging.totalCount}
          </StyledResultCount>
        )}
        {items.map((item) => {
          const itemKey = getItemKey(item);
          const isOpen = openItemKey === itemKey;
          const isSelected = selectedItemKey === itemKey;
          const detailsId = `index-search-result-${itemKey}`;
          return (
            <div key={itemKey}>
              <StyledItemButton
                type="button"
                aria-expanded={isOpen}
                aria-controls={detailsId}
                aria-pressed={isSelected}
                $selected={isSelected}
                onClick={() => toggleItem(item)}
              >
                <StyledTitle>{getTitle(item)}</StyledTitle>
                <FontAwesomeIcon icon={isOpen ? faAngleUp : faAngleDown} />
              </StyledItemButton>
              {isOpen && (
                <StyledAttributes id={detailsId}>
                  {getAttributes(item, appLayerLocale).map(
                    ({ key, displayKey, value }) => (
                      <StyledAttribute key={key}>
                        <StyledKey title={key}>{displayKey}</StyledKey>
                        <StyledValue>
                          {highlightQuery(value, query)}
                        </StyledValue>
                      </StyledAttribute>
                    )
                  )}
                </StyledAttributes>
              )}
            </div>
          );
        })}
        {status === 'loading' && (
          <StyledMessage role="status">
            {strings.search?.searching ?? 'Searching...'}
          </StyledMessage>
        )}
        {status === 'succeeded' && query && items.length === 0 && (
          <StyledMessage>{strings.search.searchIndex.noResults}</StyledMessage>
        )}
      </StyledDropDown>

      {hasNextPage && (
        <StyledShowMoreButtonWrapper>
          <StyledShowMoreButton onClick={() => loadNextPage()}>
            {strings.gfi.getMoreFeatures}
          </StyledShowMoreButton>
        </StyledShowMoreButtonWrapper>
      )}
    </>
  );
};

export default IndexSearchResultPanel;
